import React, { useLayoutEffect, useRef, useState } from "react";

const SMART_SPEED = 250;

/**
 * Minimal React stand-in for the reference's Owl Carousel 2.3.4 (jQuery plugin), rendering its
 * initialised markup so the ported reference CSS applies unchanged:
 *   div#id.{className}[.owl-rtl].owl-loaded.owl-drag
 *     > .owl-stage-outer > .owl-stage[style transform/transition/width] > .owl-item[.active] × n
 *     + .owl-nav > button.owl-prev + button.owl-next   + .owl-dots.disabled
 * Same maths as Owl: item width = (element width / items).toFixed(3), stage width = ceil(sum),
 * coordinate = ceil(±position × item width), animation speed = min(max(|Δ|,1),6) × smartSpeed,
 * non-loop maximum = count − items, `.active` = items whose start lies inside the viewport.
 * `resetKey`: like the reference's "replace.owl.carousel" on every data poll, a change jumps back
 * to position 0 without animation.
 * `slideMs`: fixed slide time instead of Owl's smartSpeed maths. `drag`: the row follows a finger and
 * snaps to the nearest whole card on release (result-modal sliders, like the AB 50 / 150 ones).
 */
export default function Dum10Owl({ id, className, items = 3, slideBy = 1, rtl = false, resetKey, slideMs, drag = false, children }) {
  const ref = useRef(null);
  const [width, setWidth] = useState(0);
  const [pos, setPos] = useState({ current: 0, speed: 0, key: resetKey });
  const slides = React.Children.toArray(children);
  const count = slides.length;

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const measure = () => setWidth(el.getBoundingClientRect().width);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // A new resetKey (data refresh) snaps back to the start, like Owl's replace + reset(0)
  let { current, speed } = pos;
  if (pos.key !== resetKey) {
    current = 0;
    speed = 0;
    setPos({ current: 0, speed: 0, key: resetKey });
  }

  const maximum = Math.max(0, count - items);
  current = Math.min(current, maximum);
  const itemWidth = width ? Number((width / items).toFixed(3)) : 0;
  const dir = rtl ? 1 : -1;
  const coordinate = Math.ceil(dir * current * itemWidth) || 0;
  const stageWidth = Math.ceil(itemWidth * count);

  const go = (next) => {
    const target = Math.max(0, Math.min(maximum, current + (next ? slideBy : -slideBy)));
    if (target === current) return;
    const s = slideMs ?? Math.min(Math.max(Math.abs(target - current), 1), 6) * SMART_SPEED;
    setPos({ current: target, speed: s, key: resetKey });
  };

  // finger drag: `current` is fractional mid-drag; release snaps to a whole card
  const touch = useRef(null);
  const [dragging, setDragging] = useState(false);
  const clamp = (p) => Math.min(maximum, Math.max(0, p));
  const dragHandlers = drag
    ? {
        onTouchStart: (e) => {
          touch.current = { x: e.touches[0].clientX, from: current };
          setDragging(true);
        },
        onTouchMove: (e) => {
          const t = touch.current;
          if (!t || !itemWidth) return;
          setPos({ current: clamp(t.from + (dir * (e.touches[0].clientX - t.x)) / itemWidth), speed: 0, key: resetKey });
        },
        onTouchEnd: () => {
          touch.current = null;
          setDragging(false);
          setPos({ current: clamp(Math.round(current)), speed: slideMs ?? SMART_SPEED, key: resetKey });
        },
      }
    : {};

  // Owl's "active" test: begin = coordinate, end = begin ± element width, item start = i × item width
  const begin = Math.abs(coordinate);
  const isActive = (i) => itemWidth > 0 && i * itemWidth >= begin && i * itemWidth < begin + width;
  const navDisabled = count <= items;

  return (
    <div
      ref={ref}
      id={id}
      className={`${className}${rtl ? " owl-rtl" : ""} owl-loaded owl-drag`}
    >
      <div
        className="owl-stage-outer"
        {...dragHandlers}
        onTouchCancel={dragHandlers.onTouchEnd}
        style={drag ? { touchAction: "pan-y" } : undefined}
      >
        <div
          className="owl-stage"
          style={{
            transform: `translate3d(${coordinate}px, 0px, 0px)`,
            transition: dragging ? "none" : `${speed / 1000}s`,
            width: `${stageWidth}px`,
          }}
        >
          {slides.map((slide, i) => (
            <div key={i} className={isActive(i) ? "owl-item active" : "owl-item"} style={{ width: `${itemWidth}px` }}>
              {slide}
            </div>
          ))}
        </div>
      </div>
      <div className={navDisabled ? "owl-nav disabled" : "owl-nav"}>
        <button type="button" role="presentation" className={current <= 0 ? "owl-prev disabled" : "owl-prev"} onClick={() => go(false)}>
          <span aria-label="Previous">‹</span>
        </button>
        <button
          type="button"
          role="presentation"
          className={current >= maximum ? "owl-next disabled" : "owl-next"}
          onClick={() => go(true)}
        >
          <span aria-label="Next">›</span>
        </button>
      </div>
      <div className="owl-dots disabled" />
    </div>
  );
}
